/**
 * Single source of truth for how we derive a human-visible property label from persisted fields.
 * Call sites receive rows after Prisma + org-at-rest decrypt; do not decrypt here.
 */

export type PropertyDisplaySource = {
  name: string;
  contents?: { title?: string | null; language?: string }[] | null;
  channelAccommodationName?: string | null;
  channelRawData?: unknown;
};

function listingNameFromChannelRawData(channelRawData: unknown): string | null {
  if (channelRawData == null || typeof channelRawData !== 'object') return null;
  const r = channelRawData as Record<string, unknown>;
  const top = r.name;
  if (typeof top === 'string' && top.trim()) return top.trim();
  const rooms = r.rooms;
  if (Array.isArray(rooms) && rooms[0] && typeof rooms[0] === 'object' && rooms[0] !== null) {
    const rn = (rooms[0] as Record<string, unknown>).name;
    if (typeof rn === 'string' && rn.trim()) return rn.trim();
  }
  const roomTypes = r.roomTypes;
  if (Array.isArray(roomTypes) && roomTypes[0] && typeof roomTypes[0] === 'object' && roomTypes[0] !== null) {
    const rn = (roomTypes[0] as Record<string, unknown>).name;
    if (typeof rn === 'string' && rn.trim()) return rn.trim();
  }
  return null;
}

/**
 * Canonical label for one property row: trimmed internal `name`, else first content title,
 * else channel listing name, else PMS JSON mirror. Same ordering everywhere.
 */
export function resolvePropertyLabel(src: PropertyDisplaySource): string {
  const trimmed = typeof src.name === 'string' ? src.name.trim() : '';
  if (trimmed) return trimmed;
  const fromContent = src.contents?.map((c) => c.title?.trim()).find(Boolean);
  if (fromContent) return fromContent;
  const ch = src.channelAccommodationName?.trim();
  if (ch) return ch.slice(0, 50);
  const pms = listingNameFromChannelRawData(src.channelRawData);
  if (pms) return pms.slice(0, 200);
  return '';
}

/**
 * When `row` is a subunit (has a parent property), combine labels. If the subunit has no
 * own label, show the parent label only (anonymous subunit).
 */
export function resolveLabelWithParent(
  row: PropertyDisplaySource,
  parent: PropertyDisplaySource | null | undefined,
): string {
  const self = resolvePropertyLabel(row);
  if (!parent) return self;
  const p = resolvePropertyLabel(parent);
  if (self.trim()) {
    if (!p.trim()) return self;
    return `${p} - ${self}`;
  }
  return p;
}

/**
 * Naam van de toegewezen unit van een boeking binnen een multi-unit accommodatie
 * (`Property.totalUnits > 1`, slotnaam uit `Property.unitNames`, terugval "Unit n").
 * Eén rekenregel voor boekingsserializer, inbox en kalender; null = geen unit-toewijzing.
 */
export function resolveBookingUnitName(
  property: { totalUnits?: number | null; unitNames?: unknown },
  unitIndex: number | null | undefined,
): string | null {
  const totalUnits = property.totalUnits ?? 1;
  if (totalUnits <= 1 || !unitIndex) return null;
  const unitNames = property.unitNames;
  const named =
    unitNames && typeof unitNames === 'object' && !Array.isArray(unitNames)
      ? (unitNames as Record<string, unknown>)[String(unitIndex)]
      : undefined;
  const trimmed = typeof named === 'string' ? named.trim() : '';
  return trimmed || `Unit ${unitIndex}`;
}

/**
 * Unit-eerst-label voor een inbox-conversatie. Twee unit-modellen:
 * - echte subunit: de boeking hangt aan een subunit-rij met parentProperty → unit = subunitnaam;
 * - multi-unit slot: boeking op de hoofdaccommodatie met `unitIndex` → naam uit `unitNames`.
 * Zonder unit-toewijzing blijft alleen de accommodatienaam over (unitName null).
 */
export function resolveThreadUnitLabel(
  prop: { totalUnits?: number | null; unitNames?: unknown } | null | undefined,
  parentProp: PropertyDisplaySource | null | undefined,
  displayName: string,
  unitIndex: number | null,
): { unitName: string | null; parentPropertyName: string | null } {
  if (parentProp) {
    const parentName = resolvePropertyLabel(parentProp);
    if (displayName.trim() && parentName.trim() && displayName.trim() !== parentName.trim()) {
      return { unitName: displayName, parentPropertyName: parentName };
    }
    return { unitName: null, parentPropertyName: null };
  }
  const slotName = prop ? resolveBookingUnitName(prop, unitIndex) : null;
  return slotName ? { unitName: slotName, parentPropertyName: displayName } : { unitName: null, parentPropertyName: null };
}

/** Legacy string-only helper; prefer {@link resolveLabelWithParent} when full rows are available. */
export function getPropertyDisplayNameWithParent(
  displayName: string,
  parentDisplayName?: string | null,
): string {
  const c = displayName?.trim() ?? '';
  const par = parentDisplayName?.trim() ?? '';
  if (!par) return c;
  if (!c) return par;
  return `${par} - ${c}`;
}
